// ─── Show popular products when the search flyout has no results ─────────────
// Uses polling (not a MutationObserver) to check for the flyout's own
// "No results" state, for the same reason as the header-relabel fix below:
// reacting synchronously to this widget's own DOM changes risks fighting
// with its render cycle.
//
// Tiles are server-rendered Liquid (badges/reviews/metafield features/stock
// all need `product.metafields` etc., none of which the public products.json
// endpoint exposes), from the hidden #algolia-popular-products-source block
// in sections/algolia-custom.liquid. That section only renders on collection
// /search pages, but this flyout can open from anywhere, so when we're not
// already on such a page we fetch a known collection page's full HTML and
// pull the block out of it — deliberately not using Shopify's section-
// rendering API (?section_id=...), since that id is auto-generated per
// theme/environment and differs between dev and production, which broke
// this for any visitor whose first-ever pageview was the homepage.
(function () {
  let sourceElementPromise = null;

  function getPopularProductsSource() {
    if (!sourceElementPromise) {
      const existing = document.getElementById(
        "algolia-popular-products-source",
      );
      if (existing) {
        sourceElementPromise = Promise.resolve(existing);
      } else {
        sourceElementPromise = fetch("/collections/all")
          .then(function (res) { return res.ok ? res.text() : ""; })
          .then(function (html) {
            const doc = new DOMParser().parseFromString(html, "text/html");
            return doc.getElementById("algolia-popular-products-source");
          })
          .catch(function () { return null; });
      }
    }
    return sourceElementPromise;
  }

  function injectPopularProducts(panel) {
    if (panel.querySelector(".aa-NoResultsPopular")) return;
    getPopularProductsSource().then(function (source) {
      const products = source
        ? source.querySelectorAll(":scope > .algolia-popular-product")
        : [];
      if (!products.length) return;
      if (panel.querySelector(".aa-NoResultsPopular")) return;

      const container = document.createElement("div");
      container.className = "aa-NoResultsPopular";
      // Reuses the real products carousel's #medihub-flyout-products-list id
      // (left unwrapped) so medihub-header.js's own MutationObserver finds it
      // and applies its usual .aa-chevron-wrap + scroll-button decoration —
      // wrapping it here too would double it up, same as it already does for
      // the real products source's header.
      container.innerHTML =
        '<div class="aa-SourceHeader"><span class="aa-SourceHeaderTitle">You might be interested in</span><div class="aa-SourceHeaderLine"></div></div>' +
        '<ul class="aa-List" role="listbox" id="medihub-flyout-products-list"></ul>';
      const list = container.querySelector("#medihub-flyout-products-list");
      products.forEach(function (product) {
        const li = document.createElement("li");
        li.className = "aa-Item";
        li.setAttribute("role", "option");
        li.setAttribute("aria-selected", "false");
        // product-grid-item-style-1 renders a bare .item-product with no
        // .product-tile wrapper, but the carousel's existing CSS expects
        // .aa-Item > .product-tile > .item-product, so it's added back here.
        li.innerHTML = '<div class="product-tile">' + product.innerHTML + "</div>";
        list.appendChild(li);
      });
      panel.appendChild(container);
    });
  }

  setInterval(function () {
    if (!document.querySelector(".aa-NoResultsHeader")) {
      // No longer in a no-results state (e.g. the query now has real
      // results) — clean up any fallback we previously injected, since the
      // widget doesn't necessarily clear this panel's contents itself.
      document.querySelectorAll(".aa-NoResultsPopular").forEach(function (el) {
        el.remove();
      });
      return;
    }
    document.querySelectorAll(".aa-NoResultsHeader").forEach(function (header) {
      const panel = header.closest(".aa-PanelLayout") || header.parentElement;
      if (panel) injectPopularProducts(panel);
    });
  }, 200);
})();

// ─── Swap search tile images to the cheapest variant's image ─────────────────
// Algolia only indexes one representative variant per product, and it isn't
// necessarily the cheapest one (unlike price, which Algolia tracks as a
// min/max range across all variants automatically). This looks up the real
// cheapest variant's image per product and swaps it in after render.
// Lives here (not in the collection/search page section) because this file
// loads on every page, and the header search flyout can open from anywhere.
(function () {
  const productCache = new Map(); // handle -> Promise<imageUrl|null>

  function fetchCheapestVariantImage(handle) {
    if (!productCache.has(handle)) {
      const promise = fetch("/products/" + handle + ".js")
        .then(function (res) { return res.ok ? res.json() : null; })
        .then(function (product) {
          if (!product || !product.variants || !product.variants.length) return null;
          const cheapest = product.variants.reduce(function (min, variant) {
            return variant.price < min.price ? variant : min;
          }, product.variants[0]);
          const imageUrl = (cheapest.featured_image && cheapest.featured_image.src) || product.featured_image || null;
          return imageUrl ? { imageUrl: imageUrl, title: product.title } : null;
        })
        .catch(function () { return null; });
      productCache.set(handle, promise);
    }
    return productCache.get(handle);
  }

  function processImage(img) {
    const handle = img.dataset.cheapestImg;
    if (!handle) return;
    // Autocomplete re-renders on every keystroke by mutating existing <img>
    // elements in place (new src/alt/handle on the same DOM node) rather
    // than replacing them. A plain one-shot "done" flag would either skip
    // reprocessing a recycled node for its new item, or — worse — let an
    // in-flight fetch from the node's PREVIOUS item resolve late and
    // overwrite the new item's already-correct image. Tracking the handle
    // this node was last processed for (instead of a boolean) fixes both:
    // a different handle means it's genuinely a new item to process.
    if (img.dataset.cheapestImgHandle === handle) return;
    img.dataset.cheapestImgHandle = handle;
    // Capture the expected title now, synchronously, before the async
    // fetch — not inside the .then(), where the node could have since
    // been recycled for a different item and img.alt would no longer
    // reflect what this fetch was actually started for.
    const expectedTitle = (img.alt || "").trim().toLowerCase();
    fetchCheapestVariantImage(handle).then(function (result) {
      if (!result) return;
      // The node may have been recycled for a different item while this
      // fetch was in flight — if its handle has moved on, this result is
      // stale and applying it would overwrite the new item's image.
      if (img.dataset.cheapestImgHandle !== handle) return;
      // Belt-and-braces: Algolia's indexed handle can itself go stale
      // (e.g. a listing repurposed from an older product) and resolve to
      // a completely different product. Only swap the image in if the
      // fetched product's title actually matches the tile.
      const fetchedTitle = (result.title || "").trim().toLowerCase();
      if (expectedTitle && fetchedTitle && expectedTitle !== fetchedTitle) {
        console.warn(
          "Skipped cheapest-variant image swap: handle \"" + handle + "\" resolved to a different product than expected.",
          { expectedTitle: expectedTitle, fetchedTitle: fetchedTitle }
        );
        return;
      }
      img.src = result.imageUrl;
    });
  }

  function scan(root) {
    root.querySelectorAll("img[data-cheapest-img]").forEach(processImage);
  }

  scan(document);

  new MutationObserver(function (mutations) {
    mutations.forEach(function (mutation) {
      // The autocomplete library re-renders on every keystroke by mutating
      // attributes on EXISTING <img> nodes (new handle/alt/src for a
      // different item) rather than always removing and re-adding DOM
      // nodes — that's an attribute change, not a childList one. Without
      // watching it directly, a recycled node's data-cheapest-img never
      // gets reprocessed, so a slower fetch started for its PREVIOUS item
      // can resolve later and overwrite the new item's already-correct
      // image, with no handle mismatch for processImage to catch.
      if (mutation.type === "attributes") {
        if (mutation.target.nodeType === 1 && mutation.target.matches("img[data-cheapest-img]")) {
          processImage(mutation.target);
        }
        return;
      }
      mutation.addedNodes.forEach(function (node) {
        if (node.nodeType !== 1) return;
        if (node.matches && node.matches("img[data-cheapest-img]")) {
          processImage(node);
        } else if (node.querySelectorAll) {
          scan(node);
        }
      });
    });
  }).observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ["data-cheapest-img"],
  });
})();

document.addEventListener("algolia.hooks.initialize", function () {
  algoliaShopify.hooks.registerHook(
    "beforeAutocompleteOptions",
    function (options) {
      // console.log("beforeAutocompleteOptions hook running");
      const recentLib = window["@algolia/autocomplete-plugin-recent-searches"];
      const recentSearchesKey = "algolia_recent_searches";
      const recentSearchesPrefixedKey = `AUTOCOMPLETE_RECENT_SEARCHES:${recentSearchesKey}`;
      const recentContainerId = "medihub-recent-searches";
      const getRecentSearches = () => {
        try {
          const raw =
            window.localStorage.getItem(recentSearchesPrefixedKey) ||
            window.localStorage.getItem(recentSearchesKey) ||
            "[]";
          const stored = JSON.parse(raw);
          if (Array.isArray(stored)) {
            return stored;
          }
          if (stored && Array.isArray(stored.items)) {
            return stored.items;
          }
          if (stored && Array.isArray(stored.recentSearches)) {
            return stored.recentSearches;
          }
          return [];
        } catch (error) {
          return [];
        }
      };
      const normalizeRecentItem = (item) => {
        const label =
          item?.label || item?.query || (typeof item === "string" ? item : "");
        return {
          label,
          url: item?.url || `/search?q=${encodeURIComponent(label)}`,
        };
      };
      const renderRecentSearches = (state) => {
        const recentItems = getRecentSearches()
          .slice(0, 3)
          .map(normalizeRecentItem)
          .filter((item) => item.label);
        const shouldShow = !state?.query && recentItems.length > 0;
        const existing = document.getElementById(recentContainerId);
        if (!shouldShow) {
          if (existing) {
            existing.remove();
          }
          return;
        }
        const panelLeft = document.querySelector(
          ".aa-DetachedOverlay .aa-PanelSection--left",
        );
        const panelLayout = document.querySelector(
          ".aa-DetachedOverlay .aa-PanelLayout",
        );
        const host = panelLeft || panelLayout;
        if (!host) {
          return;
        }
        const container = existing || document.createElement("section");
        container.id = recentContainerId;
        container.className = "aa-Source medihub-recent-searches";
        container.setAttribute(
          "data-autocomplete-source-id",
          "recentSearchesManual",
        );
        container.innerHTML = `
          <div class="aa-SourceHeader"><span class="aa-SourceHeaderTitle">Recent Searches</span><div class="aa-SourceHeaderLine"></div></div>
          <ul class="aa-List" role="listbox">
            ${recentItems
              .map(
                (item) => `
                  <li class="aa-Item">
                    <a class="aa-ItemLink aa-ItemWrapper" href="${item.url}">
                      <div class="aa-ItemContent">
                        <div class="aa-ItemContentBody">
                          <div class="aa-ItemContentTitle">${item.label}</div>
                        </div>
                      </div>
                    </a>
                  </li>
                `,
              )
              .join("")}
          </ul>
        `;
        if (!existing) {
          host.prepend(container);
        }
      };

      if (recentLib?.createLocalStorageRecentSearchesPlugin) {
        const recentSearchesPlugin =
          recentLib.createLocalStorageRecentSearchesPlugin({
            key: recentSearchesKey,
            limit: 3,
            transformSource({ source }) {
              const originalGetItems = source.getItems;
              return {
                ...source,
                getItems(params) {
                  const items = originalGetItems(params);
                  if (items?.then) {
                    return items.then((resolved) => {
                      // console.log(
                      //   "recentSearchesPlugin items:",
                      //   resolved?.length,
                      //   resolved,
                      // );
                      return resolved;
                    });
                  }
                  // console.log(
                  //   "recentSearchesPlugin items:",
                  //   items?.length,
                  //   items,
                  // );
                  return items;
                },
                templates: {
                  ...source.templates,
                  header() {
                    return '<div class="aa-SourceHeaderTitle">Recent searches</div>';
                  },
                },
              };
            },
          });
        options.plugins = [recentSearchesPlugin, ...(options.plugins || [])];
        const originalOnStateChange = options.onStateChange;
        options.onStateChange = (params) => {
          if (typeof originalOnStateChange === "function") {
            originalOnStateChange(params);
          }
          requestAnimationFrame(() => renderRecentSearches(params.state));
        };
        const originalGetSources = options.getSources;
        options.getSources = async (params) => {
          const sources = originalGetSources
            ? await originalGetSources(params)
            : [];
          const recentItems = getRecentSearches()
            .slice(0, 3)
            .map(normalizeRecentItem)
            .filter((item) => item.label);
          // console.log("Recent searches items:", recentItems);
          if (params.state.query || recentItems.length === 0) {
            return sources;
          }
          const withRecent = [
            {
              sourceId: "recentSearches",
              getItems() {
                return recentItems;
              },
              templates: {
                header() {
                  return '<div class="aa-SourceHeaderTitle">Recent searches</div>';
                },
                item({ item }) {
                  return `<a class="aa-ItemLink" href="${item.url}">${item.label}</a>`;
                },
              },
              getItemInputValue({ item }) {
                return item.label;
              },
              getItemUrl({ item }) {
                return item.url;
              },
            },
            ...sources,
          ];
          // console.log(
          //   "getSources returning:",
          //   withRecent.map((source) => source.sourceId),
          // );
          return withRecent;
        };
        const originalShouldPanelOpen = options.shouldPanelOpen;
        options.shouldPanelOpen = ({ state }) => {
          if (state.query) {
            return true;
          }
          if (state.collections.some((collection) => collection.items.length)) {
            return true;
          }
          return originalShouldPanelOpen
            ? originalShouldPanelOpen({ state })
            : false;
        };
        const originalReshape = options.reshape;
        const isRecentSource = (source) =>
          ["recentSearches", "recentSearchesPlugin"].includes(source.sourceId);
        const injectRecent = (list, recent) => [
          ...recent,
          ...list.filter((source) => !isRecentSource(source)),
        ];
        options.reshape = ({ sources }) => {
          // console.log(
          //   "reshape input sources:",
          //   sources.map((source) => source.sourceId),
          // );
          const recent = sources.filter(isRecentSource);
          const rest = sources.filter((source) => !isRecentSource(source));
          const ordered = [...recent, ...rest];
          if (!originalReshape) {
            return ordered;
          }
          let reshaped;
          try {
            reshaped = originalReshape({ sources: ordered });
            // console.log("reshape output:", reshaped);
          } catch (error) {
            // console.error("reshape error:", error);
            return ordered;
          }
          if (!Array.isArray(reshaped) || recent.length === 0) {
            return reshaped;
          }
          if (Array.isArray(reshaped[0])) {
            const [left = [], ...rightSections] = reshaped;
            const updatedLeft = injectRecent(left, recent);
            const updatedRight = rightSections.map((section) =>
              Array.isArray(section) ? injectRecent(section, []) : section,
            );
            return [updatedLeft, ...updatedRight];
          }
          return injectRecent(reshaped, recent);
        };
      } else {
        console.warn("Recent searches plugin not available on window.");
      }

      // Change the placeholder text of the autocomplete menu
      options.placeholder = "Search our products";
      // change the .aa-Panel class to .aa-Panel--custom
      options.id = "medihub-flyout";
      // set autoFocus to true
      options.autoFocus = true;
      options.openOnFocus = true;

      // optional but helpful when query is empty
      options.detachedMediaQuery = ""; //
      return options;
    },
  );
});

document.addEventListener("algolia.hooks.initialize", function () {
  algoliaShopify.hooks.registerHook(
    "beforeAutocompleteProductTemplate",
    function (
      _defaultTemplate,
      templateOptions,
      distinct,
      itemLink,
      trackSearchAttribution,
    ) {
      // Modify default options, then return them
      const { html, item, components } = templateOptions;
      // console.log("item", item);

      let ratingValue = 0;
      if (item.meta?.reviews?.rating) {
        try {
          const ratingData = JSON.parse(item.meta.reviews.rating);
          ratingValue = parseFloat(ratingData.value) || 0;
        } catch (error) {
          console.error("Error parsing rating data:", error);
        }
      }
      return html`
        <div class="product-tile">
          <div class="item-product">
            <div class="thumbnail-container has-multiimage">
              <a
                href="${itemLink}"
                class="aa-ItemLink aa-ProductItem"
                onClick="${() => trackSearchAttribution(item)}"
              >
                <div class="respone_image" style="padding-top:100.0%;">
                  <img
                    class="product__thumbnail lazyautosizes lazyloaded"
                    src="${item.image}"
                    data-cheapest-img="${item.handle}"
                    alt="${item.title}"
                    sizes="212px"
                  />
                </div>
              </a>
            </div>

            <div class="product__info">
              <div class="product__title">
                <a
                  href="${algoliaShopify.helpers.instantsearchLink(item)}"
                  title="${algoliaShopify.helpers.fullTitle(
                    item.title,
                    item._distinct,
                  )}"
                >
                  ${components.Highlight({ hit: item, attribute: "title" })}
                </a>
              </div>

              <div class="stock-reviews">

              <!-- Availability + Reviews -->
              <div class="available_product d-flex">
                <span
                  class="product__available ${item.inventory_available
                    ? "in-stock"
                    : "sold-out"}"
                >
                  <span
                    >${item.inventory_available
                      ? "In stock"
                      : "Out of stock"}</span
                  >
                </span>

              </div>
                ${item.meta?.reviews?.rating
                  ? html`
                      <div class="mh-stars" style="--rating: ${ratingValue}" aria-label="${ratingValue.toFixed(2)} out of 5 stars">
                        <span class="mh-stars__empty">★★★★★</span>
                        <span class="mh-stars__filled">★★★★★</span>
                      </div>
                      ${item.meta.reviews.rating_count ? html`<span class="review-count">(${item.meta.reviews.rating_count})</span>` : ""}
                    `
                  : ""}

              </div>

              <div class="price-box">
                <div class="price-sale">
                  <span class="special-price">
                    ${(() => {
                      const priceString = algoliaShopify.helpers.displayPrice(
                        item,
                        distinct,
                      );
                      const priceParts = priceString
                        .replace(/<\/?[^>]+(>|$)/g, "")
                        .split(" - ");
                      return priceParts.length > 1
                        ? `from ${priceParts[0]}`
                        : priceParts[0];
                    })()}
                  </span>
                  ${item.meta?.custom?.rrp
                    ? html`
                        <div class="ais-hit--rrp">
                          <span class="ais-hit--rrp-price">
                            $${Number(item.meta.custom.rrp).toFixed(2)}
                          </span>
                          <span class="ais-hit--rrp-label">RRP</span>
                        </div>
                      `
                    : ""}
                  ${item.compare_at_price
                    ? html` <span class="old-price"
                        >was
                        ${algoliaShopify.helpers.displayStrikedPrice(
                          item.price,
                          item.compare_at_price,
                        )}</span
                      >`
                    : ""}
                </div>
              </div>
            </div>
          </div>
        </div>
      `;
    },
  );
});
